import authService from "@lib/services/AuthService";

import { URLS } from "@constants/urls";
import userRoles from "@constants/userRoles";

import { getStoredTourId, removeStoredTourId, storeTourId } from "./toursLocalStorage";
import { ADMIN_CARD_TOUR_CARD_IDS, CHECKLISTS_TO_TOURS_MAP, TOUR_ITEMS_IDS } from "../constants";
import { CurrentCardId, ProductFruitsApi } from "../types";

export const registerEvents = (
  api: ProductFruitsApi,
  setShowProductFruitsChecklistCallback: (value: boolean) => void,
  setIsProductFruitsSwitchToAdminTourRunningCallback: (value: boolean) => void,
  navigateCallback: (path: string) => void,
): void => {
  const resumePendingTourIfExists = (): void => {
    const storedTourId = getStoredTourId();
    const isAdministrator = authService.getDefaultRole() === userRoles.ADMINISTRATOR;

    const shouldResumeTour = storedTourId && isAdministrator;

    if (shouldResumeTour) {
      removeStoredTourId();
      api.tours.tryStartTour(storedTourId);
    }
  };

  const handleTourSkipped = (tourId: number): void => {
    if (tourId === TOUR_ITEMS_IDS.SWITCH_TO_ADMIN) {
      removeStoredTourId();
      setIsProductFruitsSwitchToAdminTourRunningCallback(false);
    }
  };

  const handleTourAdvanced = (tourId: number, currentCardId: CurrentCardId): void => {
    if (ADMIN_CARD_TOUR_CARD_IDS.includes(currentCardId.currentCardId)) {
      setIsProductFruitsSwitchToAdminTourRunningCallback(true);
    } else if (tourId !== TOUR_ITEMS_IDS.SWITCH_TO_ADMIN) {
      setIsProductFruitsSwitchToAdminTourRunningCallback(false);
    }
  };

  const handleTourFinished = (tourId: number): void => {
    if (tourId !== TOUR_ITEMS_IDS.SWITCH_TO_ADMIN) {
      return;
    }

    setIsProductFruitsSwitchToAdminTourRunningCallback(false);

    setTimeout(() => {
      const isAdministrator = authService.getDefaultRole() === userRoles.ADMINISTRATOR;
      const storedTourId = getStoredTourId();

      if (storedTourId && isAdministrator) {
        removeStoredTourId();
        api.tours.tryStartTour(storedTourId);
      }
    }, 2000);
  };

  const handleItemLaunched = (_id: string, internalId: string): void => {
    setShowProductFruitsChecklistCallback(false);

    // Handle the case where a tour has been launched but the user is not an admin
    const isAdministrator = authService.getDefaultRole() === userRoles.ADMINISTRATOR;

    // If the user is an administrator, proceed with the normal flow
    if (isAdministrator) {
      navigateCallback(URLS.dashboard);
      return;
    }

    const runningTourId = CHECKLISTS_TO_TOURS_MAP[internalId];
    if (!runningTourId) return;

    // Prevent the tour from running and store it for later
    api.tours.markAs(runningTourId, "skipped", true);
    storeTourId(runningTourId.toString());

    // Force user to switch to admin
    api.tours.tryStartTour(TOUR_ITEMS_IDS.SWITCH_TO_ADMIN);

    setIsProductFruitsSwitchToAdminTourRunningCallback(true);
  };

  resumePendingTourIfExists();

  api.tours.listen("tour-skipped", handleTourSkipped, []);
  api.tours.listen("tour-finished", handleTourFinished, []);
  api.tours.listen("tour-advanced", handleTourAdvanced, []);
  api.checklists.listen("item-launched", handleItemLaunched, []);
};
